// End Users (rbac-restore R3 3.3): your app's external end-users over
// gateway/api/end_users.py and the connect-link mint in gateway/api/connect.py.
//
//   GET    /end-users?limit&cursor&prefix         manage_end_users
//   POST   /end-users                             manage_end_users + platform.end_users (+ end_users.max)
//   PATCH  /end-users/{external_id}               manage_end_users        {metadata?, disabled?}
//   DELETE /end-users/{external_id}               manage_end_users        cascades accounts/triggers
//   POST   /end-users/{external_id}/connect-links manage_end_users + platform.end_users
//
// Every route needs `manage_end_users`, so without it the page fires no request. A minted
// connect link is single-open and is shown exactly once: it lives only in the dialog's
// state and is gone when the dialog closes.
import { useState, type FormEvent } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type {
  AuthConfigsResponse,
  ConnectLink,
  ConnectLinkCreate,
  EndUser,
  EndUserCreate,
  EndUsersResponse,
  EndUserUpdate,
} from '../api/types'
import { useSession } from '../context/SessionContext'
import { useCan } from '../rbac'
import { useCapacity, useFeature, type Gate } from '../entitlements'
import { PlanNote } from '../entitlements/PlanNote'
import { ConfirmAction } from '../components/ConfirmAction'
import { CopyButton } from '../components/CopyButton'
import { Icon } from '../components/Icon'
import { Modal } from '../components/Modal'
import { formatDate, formatDateTime } from '../components/format'
import { Badge, EmptyState, NotAuthorized, PageHeader, QueryState } from '../components/ui'

// gateway/identity/end_users.py valid_external_id (D6); the server re-checks.
const EXTERNAL_ID_RE = /^[A-Za-z0-9._:@+-]{1,128}$/
const PAGE = 50
const TTL_OPTIONS: [number, string][] = [
  [600, '10 minutes'],
  [1800, '30 minutes'],
]

const path = (externalId: string) => `/end-users/${encodeURIComponent(externalId)}`

/** Parse an optional metadata JSON object. '' ⇒ undefined; bad JSON ⇒ an error string. */
function parseMetadata(text: string): { value?: Record<string, unknown>; error?: string } {
  if (!text.trim()) return {}
  try {
    const v: unknown = JSON.parse(text)
    if (!v || typeof v !== 'object' || Array.isArray(v)) return { error: 'Metadata must be a JSON object.' }
    return { value: v as Record<string, unknown> }
  } catch {
    return { error: 'Metadata must be valid JSON.' }
  }
}

function useInvalidate() {
  const qc = useQueryClient()
  const { workspaceId } = useSession()
  return () => {
    void qc.invalidateQueries({ queryKey: ['end-users', workspaceId] })
    void qc.invalidateQueries({ queryKey: qk.entitlements(workspaceId) })
  }
}

function CreateModal({ onClose }: { onClose: () => void }) {
  const invalidate = useInvalidate()
  const [externalId, setExternalId] = useState('')
  const [metadata, setMetadata] = useState('')
  const [touched, setTouched] = useState(false)
  const create = useMutation({
    mutationFn: (body: EndUserCreate) => apiFetch<EndUser>('/end-users', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      invalidate()
      onClose()
    },
  })
  const idError = !externalId ? 'Required' : EXTERNAL_ID_RE.test(externalId) ? null : 'Use 1–128 of A–Z a–z 0–9 . _ : @ + -'
  const meta = parseMetadata(metadata)
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (idError || meta.error || create.isPending) return
    create.mutate({ external_id: externalId, ...(meta.value ? { metadata: meta.value } : {}) })
  }
  return (
    <Modal title="New end user" onClose={onClose}>
      <form onSubmit={onSubmit} aria-label="New end user form" noValidate>
        <div className="field">
          <label htmlFor="eu-id">External ID</label>
          <input id="eu-id" value={externalId} onChange={(e) => setExternalId(e.target.value)} maxLength={128} placeholder="e.g. user_8412" />
          <span className="field-hint">Your app’s opaque id for this user. Don’t use emails or other personal data.</span>
          {touched && idError && <span className="form-error" role="alert">{idError}</span>}
        </div>
        <div className="field">
          <label htmlFor="eu-meta">Metadata (optional JSON object)</label>
          <textarea id="eu-meta" rows={3} value={metadata} onChange={(e) => setMetadata(e.target.value)} placeholder='{"tier": "gold"}' />
          {touched && meta.error && <span className="form-error" role="alert">{meta.error}</span>}
        </div>
        {create.error ? <p className="form-error" role="alert">{errorMessage(create.error)}</p> : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={create.isPending} aria-busy={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function MetadataModal({ user, onClose }: { user: EndUser; onClose: () => void }) {
  const invalidate = useInvalidate()
  const [metadata, setMetadata] = useState(JSON.stringify(user.metadata ?? {}, null, 2))
  const meta = parseMetadata(metadata)
  const save = useMutation({
    mutationFn: (body: EndUserUpdate) => apiFetch<EndUser>(path(user.external_id), { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      invalidate()
      onClose()
    },
  })
  return (
    <Modal title={`Metadata for ${user.external_id}`} onClose={onClose}>
      <form
        aria-label="Edit metadata form"
        onSubmit={(e) => {
          e.preventDefault()
          if (meta.error || save.isPending) return
          save.mutate({ metadata: meta.value ?? {} })
        }}
      >
        <div className="field">
          <label htmlFor="eu-meta-edit">Metadata (JSON object)</label>
          <textarea id="eu-meta-edit" rows={6} value={metadata} onChange={(e) => setMetadata(e.target.value)} />
          {meta.error && <span className="form-error" role="alert">{meta.error}</span>}
        </div>
        {save.error ? <p className="form-error" role="alert">{errorMessage(save.error)}</p> : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!!meta.error || save.isPending} aria-busy={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function ConnectLinkModal({ user, onClose }: { user: EndUser; onClose: () => void }) {
  const { workspaceId } = useSession()
  // Provider choices come from the auth configs this workspace can use (view_servers).
  const configs = useQuery({
    queryKey: qk.authConfigs(workspaceId),
    queryFn: () => apiFetch<AuthConfigsResponse>('/auth-configs'),
    enabled: !!workspaceId,
    retry: false,
  })
  const providers = [...new Set((configs.data?.auth_configs ?? []).filter((c) => c.enabled).map((c) => c.provider))].sort()
  const [provider, setProvider] = useState('')
  const [ttl, setTtl] = useState(600)
  const [redirectUrl, setRedirectUrl] = useState('')
  const mint = useMutation({
    mutationFn: (body: ConnectLinkCreate) =>
      apiFetch<ConnectLink>(`${path(user.external_id)}/connect-links`, { method: 'POST', body: JSON.stringify(body) }),
  })
  const link = mint.data
  if (link) {
    return (
      <Modal
        title="Connect link created"
        onClose={onClose}
        footer={<button type="button" className="btn btn--primary" onClick={onClose}>Done</button>}
      >
        <div className="secret-once">
          <p className="warn-note" role="note">
            Copy this link now. It is shown once, works for one open only, and whoever completes consent on it connects
            their {link.provider} account for <strong>{link.external_id}</strong>.
          </p>
          <pre aria-label="Connect link URL">{link.url}</pre>
          <CopyButton value={link.url} label="Copy link" />
          <p className="cell-sub">Expires {formatDateTime(link.expires_at)}.</p>
        </div>
      </Modal>
    )
  }
  const chosen = provider || providers[0] || ''
  return (
    <Modal title={`Connect link for ${user.external_id}`} onClose={onClose}>
      <form
        aria-label="Connect link form"
        onSubmit={(e) => {
          e.preventDefault()
          if (!chosen || mint.isPending) return
          mint.mutate({ provider: chosen, ttl_seconds: ttl, ...(redirectUrl.trim() ? { redirect_url: redirectUrl.trim() } : {}) })
        }}
      >
        <div className="field">
          <label htmlFor="cl-provider">Provider</label>
          {providers.length > 0 ? (
            <select id="cl-provider" value={chosen} onChange={(e) => setProvider(e.target.value)}>
              {providers.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          ) : (
            <input id="cl-provider" value={provider} onChange={(e) => setProvider(e.target.value)} placeholder="e.g. google" />
          )}
        </div>
        <div className="field">
          <label htmlFor="cl-ttl">Expires after</label>
          <select id="cl-ttl" value={ttl} onChange={(e) => setTtl(Number(e.target.value))}>
            {TTL_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="cl-redirect">Redirect URL after connecting (optional)</label>
          <input id="cl-redirect" value={redirectUrl} onChange={(e) => setRedirectUrl(e.target.value)} placeholder="https://app.example.com/connected" />
          <span className="field-hint">Must be https on an origin in the workspace’s redirect allowlist.</span>
        </div>
        {mint.error ? <p className="form-error" role="alert">{errorMessage(mint.error)}</p> : null}
        <div className="modal-foot modal-foot--inline">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={!chosen || mint.isPending} aria-busy={mint.isPending}>
            {mint.isPending ? 'Creating…' : 'Create link'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

type Pending = { kind: 'delete' | 'disable'; user: EndUser } | null

function ConfirmModal({ pending, onClose }: { pending: NonNullable<Pending>; onClose: () => void }) {
  const invalidate = useInvalidate()
  const { user, kind } = pending
  const act = useMutation({
    mutationFn: () =>
      kind === 'delete'
        ? apiFetch(path(user.external_id), { method: 'DELETE' })
        : apiFetch<EndUser>(path(user.external_id), { method: 'PATCH', body: JSON.stringify({ disabled: true }) }),
    onSuccess: () => {
      invalidate()
      onClose()
    },
  })
  return kind === 'delete' ? (
    <ConfirmAction
      title="Delete end user"
      confirmLabel="Delete"
      pendingLabel="Deleting…"
      pending={act.isPending}
      error={act.error ? errorMessage(act.error) : null}
      onConfirm={() => act.mutate()}
      onClose={onClose}
    >
      <p>
        Delete <strong>{user.external_id}</strong>? Their connected accounts, triggers and open connect links are deleted
        too. This can’t be undone.
      </p>
    </ConfirmAction>
  ) : (
    <ConfirmAction
      title="Disable end user"
      confirmLabel="Disable"
      pendingLabel="Disabling…"
      pending={act.isPending}
      error={act.error ? errorMessage(act.error) : null}
      onConfirm={() => act.mutate()}
      onClose={onClose}
    >
      <p>
        Disable <strong>{user.external_id}</strong>? Their tools stop running and new connect links are refused. Their
        accounts are kept; you can enable them again.
      </p>
    </ConfirmAction>
  )
}

function EnableButton({ user }: { user: EndUser }) {
  const invalidate = useInvalidate()
  const enable = useMutation({
    mutationFn: () => apiFetch<EndUser>(path(user.external_id), { method: 'PATCH', body: JSON.stringify({ disabled: false }) }),
    onSuccess: invalidate,
  })
  return (
    <>
      <button type="button" className="btn btn--sm" disabled={enable.isPending} aria-busy={enable.isPending} onClick={() => enable.mutate()} aria-label={`Enable ${user.external_id}`}>
        Enable
      </button>
      {enable.error ? <span className="form-error" role="alert">{errorMessage(enable.error)}</span> : null}
    </>
  )
}

function EndUsersList({ platform, capacity }: { platform: Gate; capacity: Gate }) {
  const { workspaceId } = useSession()
  const [prefix, setPrefix] = useState('')
  const [creating, setCreating] = useState(false)
  const [linkFor, setLinkFor] = useState<EndUser | null>(null)
  const [metaFor, setMetaFor] = useState<EndUser | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const filter = EXTERNAL_ID_RE.test(prefix) ? prefix : ''
  const q = useInfiniteQuery({
    queryKey: qk.endUsers(workspaceId, filter),
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(PAGE) })
      if (pageParam) params.set('cursor', pageParam)
      if (filter) params.set('prefix', filter)
      return apiFetch<EndUsersResponse>(`/end-users?${params}`)
    },
    initialPageParam: '' as string,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: !!workspaceId,
  })
  const users = q.data?.pages.flatMap((p) => p.end_users) ?? []
  const createGate = !platform.allowed ? platform : capacity

  return (
    <>
      <div className="toolbar">
        <input
          type="search"
          className="toolbar-grow"
          aria-label="Filter by external ID prefix"
          placeholder="Filter by external ID prefix"
          value={prefix}
          onChange={(e) => setPrefix(e.target.value)}
        />
        <button type="button" className="btn btn--primary" disabled={!createGate.allowed} onClick={() => setCreating(true)}>
          <Icon name="plus" size={14} /> New end user
        </button>
      </div>
      {platform.allowed && <PlanNote gate={capacity}>End-user limit reached.</PlanNote>}

      <QueryState isLoading={q.isLoading} error={q.error} what="end users" onRetry={() => q.refetch()}>
        {users.length === 0 ? (
          <EmptyState
            icon="users"
            title={filter ? 'No end users match' : 'No end users yet'}
            body="End users are the people using your app. Create one, or mint a connect link and Symone creates it for you."
          />
        ) : (
          <div className="table-wrap">
            <table className="table" aria-label="End users">
              <thead>
                <tr>
                  <th>External ID</th>
                  <th>Status</th>
                  <th>Metadata</th>
                  <th>Created</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="cell-strong"><code>{u.external_id}</code></td>
                    <td>{u.disabled ? <Badge tone="warning">Disabled</Badge> : <Badge tone="success">Active</Badge>}</td>
                    <td className="cell-sub">{Object.keys(u.metadata ?? {}).length ? Object.keys(u.metadata).join(', ') : '—'}</td>
                    <td>{formatDate(u.created_at)}</td>
                    <td className="cell-actions">
                      <button
                        type="button"
                        className="btn btn--sm"
                        disabled={!platform.allowed || u.disabled}
                        title={platform.reason || (u.disabled ? 'Enable this end user first' : undefined)}
                        onClick={() => setLinkFor(u)}
                        aria-label={`Connect link for ${u.external_id}`}
                      >
                        Connect link
                      </button>
                      <button type="button" className="btn btn--sm" onClick={() => setMetaFor(u)} aria-label={`Edit metadata for ${u.external_id}`}>
                        Metadata
                      </button>
                      {u.disabled ? (
                        <EnableButton user={u} />
                      ) : (
                        <button type="button" className="btn btn--sm" onClick={() => setPending({ kind: 'disable', user: u })} aria-label={`Disable ${u.external_id}`}>
                          Disable
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost-danger"
                        onClick={() => setPending({ kind: 'delete', user: u })}
                        aria-label={`Delete ${u.external_id}`}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {q.hasNextPage && (
              <div className="table-foot">
                <button type="button" className="btn btn--sm" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                  {q.isFetchingNextPage ? 'Loading…' : 'Load more'}
                </button>
              </div>
            )}
          </div>
        )}
      </QueryState>

      {creating && <CreateModal onClose={() => setCreating(false)} />}
      {linkFor && <ConnectLinkModal user={linkFor} onClose={() => setLinkFor(null)} />}
      {metaFor && <MetadataModal user={metaFor} onClose={() => setMetaFor(null)} />}
      {pending && <ConfirmModal pending={pending} onClose={() => setPending(null)} />}
    </>
  )
}

function Gated() {
  const platform = useFeature('platform.end_users')
  const capacity = useCapacity('end_users.max', 'end_users')
  return (
    <>
      <PlanNote gate={platform}>End users and connect links:</PlanNote>
      <EndUsersList platform={platform} capacity={capacity} />
    </>
  )
}

export function EndUsersView() {
  const canManage = useCan('manage_end_users')
  return (
    <div className="page">
      <PageHeader
        title="End Users"
        description="The people using your app. Each one gets their own connected accounts, which agents act on through tool sessions."
      />
      {canManage ? <Gated /> : <NotAuthorized what="end users" />}
    </div>
  )
}
